import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Share, Plus, Smartphone, CheckCircle, ArrowRight, Apple, Laptop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/usePWA';
import { SEOHead } from '@/components/seo/SEOHead';
import aiveliqLogo from '@/assets/aiveliq360-logo.png';

export default function Install() {
  const { canInstall, installPWA, isIOS, isAndroid, isInstalled, isStandalone } = usePWA();
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    await installPWA();
    setInstalling(false);
  };

  const features = [
    { icon: CheckCircle, text: 'Works offline with cached data' },
    { icon: CheckCircle, text: 'Fast loading and native feel' },
    { icon: CheckCircle, text: 'Push notifications for updates' },
    { icon: CheckCircle, text: 'Quick access from home screen' },
    { icon: CheckCircle, text: 'Automatic updates' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Install Aiveliq360 App | Get the Native Experience"
        description="Install Aiveliq360 on your device for quick access, offline support, and a native app experience."
      />

      {/* Header */}
      <header className="border-b border-border/50 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-8 object-contain" />
          </Link>
          <Link to="/app">
            <Button variant="ghost" size="sm">
              Go to Dashboard
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {isInstalled || isStandalone ? (
          /* Already Installed State */
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-success/10 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-success" />
            </div>
            <h1 className="text-3xl font-bold mb-3">App Installed!</h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Aiveliq360 is already installed on your device. Enjoy the native app experience!
            </p>
            <Link to="/app">
              <Button size="lg" className="gap-2">
                Open Dashboard
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          /* Installation Options */
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Info */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Smartphone className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Progressive Web App</span>
              </div>

              <h1 className="text-4xl font-bold mb-4">
                Install Aiveliq360 on Your Device
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Get the full native app experience without downloading from an app store. 
                Quick access, offline support, and seamless performance.
              </p>

              <ul className="space-y-3 mb-8">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <feature.icon className="h-5 w-5 text-success shrink-0" />
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>

              {canInstall && (
                <Button 
                  size="lg" 
                  onClick={handleInstall}
                  disabled={installing}
                  className="gap-2"
                >
                  <Download className="h-5 w-5" />
                  {installing ? 'Installing...' : 'Install Now'}
                </Button>
              )}
            </div>

            {/* Right: Platform-specific instructions */}
            <div className="space-y-6">
              {/* iOS Instructions */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <Apple className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold">iPhone & iPad</h3>
                    <p className="text-sm text-muted-foreground">Safari browser required</p>
                  </div>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">1</span>
                    <span>Tap the <Share className="h-4 w-4 inline mx-1" /> Share button in Safari</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">2</span>
                    <span>Scroll down and tap <Plus className="h-4 w-4 inline mx-1" /> "Add to Home Screen"</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">3</span>
                    <span>Tap "Add" to confirm installation</span>
                  </li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <Smartphone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Android</h3>
                    <p className="text-sm text-muted-foreground">Chrome browser recommended</p>
                  </div>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">1</span>
                    <span>Tap the menu icon (⋮) in Chrome</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">2</span>
                    <span>Tap "Install app" or "Add to Home screen"</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">3</span>
                    <span>Tap "Install" to confirm</span>
                  </li>
                </ol>
              </div>

              {/* Desktop Instructions */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <Laptop className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Desktop</h3>
                    <p className="text-sm text-muted-foreground">Chrome, Edge, or Brave</p>
                  </div>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">1</span>
                    <span>Look for the install icon in the address bar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">2</span>
                    <span>Click "Install" when prompted</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
